import React, { useEffect, useRef, useState } from 'react';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import { useSelector } from 'react-redux';

import { classCleanup, sleep } from '../helpers';
import { checkA11y } from '../validators';

import ResultsCard from './ResultsCard';

const CONTENT_ID = 'page-document';

// Tempo necessário para a página terminar de renderizar (blocos carregados sob
// demanda) antes de auditar.
const RENDER_DELAY = 3000;

// Teto para a espera pelo conteúdo da rota, para o painel não ficar preso em
// "analisando" caso a requisição falhe.
const CONTENT_TIMEOUT = 60000;
const CONTENT_POLL_INTERVAL = 250;

const normalizePath = (path) => (path || '').replace(/\/+$/, '') || '/';

const AxeCoreValidator = ({ pathname }) => {
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [waitingContent, setWaitingContent] = useState(true);

  // Em navegação client-side o reducer mantém o conteúdo da página anterior até
  // a nova requisição terminar, então também comparamos o caminho.
  const isContentReady = useSelector((state) => {
    const { get, data } = state.content || {};
    const id = data?.['@id'];
    if (!get?.loaded || get?.loading || !id) return false;
    return normalizePath(pathname).endsWith(normalizePath(flattenToAppURL(id)));
  });

  const isContentReadyRef = useRef(isContentReady);
  useEffect(() => {
    isContentReadyRef.current = isContentReady;
  }, [isContentReady]);

  useEffect(() => {
    let cancelled = false;

    const waitForContent = async () => {
      const deadline = Date.now() + CONTENT_TIMEOUT;
      while (!isContentReadyRef.current && !cancelled) {
        if (Date.now() >= deadline) return;
        await sleep(CONTENT_POLL_INTERVAL);
      }
    };

    const fetchContentAndCheckA11y = async () => {
      setErrors([]);
      setLoading(true);
      setWaitingContent(true);
      classCleanup();

      await waitForContent();
      if (cancelled) return;
      setWaitingContent(false);

      await sleep(RENDER_DELAY);
      if (cancelled) return;

      // Buscado só agora: o React remonta o elemento durante a renderização e
      // uma referência obtida antes ficaria desconectada do documento.
      const content = document.getElementById(CONTENT_ID);
      if (!content) {
        setErrors([]);
        setLoading(false);
        return;
      }

      const foundErrors = await checkA11y(content);
      if (!cancelled) {
        setErrors(foundErrors);
        setLoading(false);
      }
    };

    fetchContentAndCheckA11y();

    return () => {
      cancelled = true;
      classCleanup();
    };
  }, [pathname]);

  // Enquanto o conteúdo da nova rota não chega, o Volto ainda exibe a página
  // anterior; mostrar o painel aqui daria a impressão de que ela está sendo
  // analisada.
  if (waitingContent) return null;

  return loading ? (
    <section
      className="a11y-panel a11y-panel--loading a11y-loading"
      role="status"
      aria-live="polite"
    >
      <div className="a11y-panel__icon" aria-hidden="true">
        <span className="fa-solid fa-spinner fa-spin" />
      </div>
      <div className="a11y-panel__content">
        <div className="a11y-loading-title">Analisando acessibilidade</div>
        <p className="a11y-loading-message">
          Estamos avaliando os elementos da página. Em instantes a lista de
          verificações ficará disponível.
        </p>
        <div className="a11y-loading-progress" aria-hidden="true">
          <span className="a11y-loading-progress__bar" />
        </div>
      </div>
    </section>
  ) : errors && errors.length ? (
    <ResultsCard errors={errors} />
  ) : null;
};

export default AxeCoreValidator;
